/**
 * redux/slices/dashboard.ts — analytics dashboard data store (Phase 2).
 *
 * One AsyncCell per page (Overview, Loans, Deposits, NPA, Products,
 * Branches, Data Quality) plus three drill-in caches keyed by id so
 * Browser-back doesn't trigger a refetch:
 *
 *   bankDetail    keyed by orgId
 *   branchDetail  keyed by `orgId:branchIdLocal`
 *   productDetail keyed by `type:productCode:date?`
 *
 * Filters live in component-local useState (PHASE2_SPEC §6) — they are not
 * stored here.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import * as apiClient from '../../services/api';
import type {
	AsyncCell,
	BankDetailResponse,
	BankListResponse,
	BranchDetailResponse,
	BranchesFilters,
	BranchesResponse,
	DataQualityFilters,
	DataQualityResponse,
	DepositFilters,
	DepositsResponse,
	LoanFilters,
	LoansResponse,
	NPAFilters,
	NPAResponse,
	OverviewResponse,
	ProductDetailResponse,
	ProductType,
	ProductsFilters,
	ProductsResponse,
} from '../../type/api';
import type { RootState } from '../store';

const emptyCell = <T,>(): AsyncCell<T> => ({ data: null, status: 'idle', error: null });

export interface DashboardState {
	overview: AsyncCell<OverviewResponse>;
	loans: AsyncCell<LoansResponse>;
	deposits: AsyncCell<DepositsResponse>;
	npa: AsyncCell<NPAResponse>;
	products: AsyncCell<ProductsResponse>;
	branches: AsyncCell<BranchesResponse>;
	dataQuality: AsyncCell<DataQualityResponse>;
	bankList: AsyncCell<BankListResponse>;
	bankDetail: Record<number, AsyncCell<BankDetailResponse>>;
	branchDetail: Record<string, AsyncCell<BranchDetailResponse>>;
	productDetail: Record<string, AsyncCell<ProductDetailResponse>>;
}

const INITIAL: DashboardState = {
	overview: emptyCell<OverviewResponse>(),
	loans: emptyCell<LoansResponse>(),
	deposits: emptyCell<DepositsResponse>(),
	npa: emptyCell<NPAResponse>(),
	products: emptyCell<ProductsResponse>(),
	branches: emptyCell<BranchesResponse>(),
	dataQuality: emptyCell<DataQualityResponse>(),
	bankList: emptyCell<BankListResponse>(),
	bankDetail: {},
	branchDetail: {},
	productDetail: {},
};

// ── Helpers ─────────────────────────────────────────────────────────────

function errMsg(err: unknown, fallback: string): string {
	return err instanceof Error ? err.message : fallback;
}

export function branchKey(orgId: number, branchIdLocal: string): string {
	return `${orgId}:${branchIdLocal}`;
}

export function productKey(type: ProductType, productCode: string, date?: string): string {
	return `${type}:${productCode}:${date ?? ''}`;
}

// ── Thunks ──────────────────────────────────────────────────────────────

export const fetchOverview = createAsyncThunk<
	OverviewResponse,
	{ date?: string } | void,
	{ rejectValue: string }
>('dashboard/overview', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getOverview(arg ? arg.date : undefined);
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load overview'));
	}
});

export const fetchLoans = createAsyncThunk<
	LoansResponse,
	LoanFilters | void,
	{ rejectValue: string }
>('dashboard/loans', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getLoans(arg || {});
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load loans'));
	}
});

export const fetchDeposits = createAsyncThunk<
	DepositsResponse,
	DepositFilters | void,
	{ rejectValue: string }
>('dashboard/deposits', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getDeposits(arg || {});
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load deposits'));
	}
});

export const fetchNPA = createAsyncThunk<
	NPAResponse,
	NPAFilters | void,
	{ rejectValue: string }
>('dashboard/npa', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getNPA(arg || {});
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load NPA data'));
	}
});

export const fetchProducts = createAsyncThunk<
	ProductsResponse,
	ProductsFilters | void,
	{ rejectValue: string }
>('dashboard/products', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getProducts(arg || {});
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load products'));
	}
});

export const fetchBranches = createAsyncThunk<
	BranchesResponse,
	BranchesFilters | void,
	{ rejectValue: string }
>('dashboard/branches', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getBranches(arg || {});
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load branches'));
	}
});

export const fetchDataQuality = createAsyncThunk<
	DataQualityResponse,
	DataQualityFilters | void,
	{ rejectValue: string }
>('dashboard/dataQuality', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getDataQuality(arg || {});
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load data quality'));
	}
});

export const fetchBankList = createAsyncThunk<
	BankListResponse,
	{ date?: string } | void,
	{ rejectValue: string }
>('dashboard/bankList', async (arg, { rejectWithValue }) => {
	try {
		return await apiClient.getBankList(arg ? arg.date : undefined);
	} catch (err) {
		return rejectWithValue(errMsg(err, 'Failed to load banks'));
	}
});

export const fetchBankDetail = createAsyncThunk<
	{ orgId: number; data: BankDetailResponse },
	{ orgId: number; date?: string },
	{ rejectValue: { orgId: number; message: string } }
>('dashboard/bankDetail', async (arg, { rejectWithValue }) => {
	try {
		const data = await apiClient.getBankDetail(arg.orgId, arg.date);
		return { orgId: arg.orgId, data };
	} catch (err) {
		return rejectWithValue({ orgId: arg.orgId, message: errMsg(err, 'Failed to load bank detail') });
	}
});

export const fetchBranchDetail = createAsyncThunk<
	{ key: string; data: BranchDetailResponse },
	{ orgId: number; branchIdLocal: string; date?: string },
	{ rejectValue: { key: string; message: string } }
>('dashboard/branchDetail', async (arg, { rejectWithValue }) => {
	const key = branchKey(arg.orgId, arg.branchIdLocal);
	try {
		const data = await apiClient.getBranchDetail(arg.orgId, arg.branchIdLocal, arg.date);
		return { key, data };
	} catch (err) {
		return rejectWithValue({ key, message: errMsg(err, 'Failed to load branch detail') });
	}
});

export const fetchProductDetail = createAsyncThunk<
	{ key: string; data: ProductDetailResponse },
	{ productCode: string; type: ProductType; date?: string; orgIds?: number[] },
	{ rejectValue: { key: string; message: string } }
>('dashboard/productDetail', async (arg, { rejectWithValue }) => {
	const key = productKey(arg.type, arg.productCode, arg.date);
	try {
		const data = await apiClient.getProductDetail(arg.productCode, arg.type, arg.date, arg.orgIds);
		return { key, data };
	} catch (err) {
		return rejectWithValue({ key, message: errMsg(err, 'Failed to load product detail') });
	}
});

// ── Slice ───────────────────────────────────────────────────────────────

const dashboardSlice = createSlice({
	name: 'dashboard',
	initialState: INITIAL,
	reducers: {},
	extraReducers: (builder) => {
		builder
			// Overview
			.addCase(fetchOverview.pending, (s) => {
				s.overview.status = 'loading';
				s.overview.error = null;
			})
			.addCase(fetchOverview.fulfilled, (s, a) => {
				s.overview.status = 'success';
				s.overview.data = a.payload;
				s.overview.error = null;
			})
			.addCase(fetchOverview.rejected, (s, a) => {
				s.overview.status = 'error';
				s.overview.error = a.payload || 'Failed to load overview';
			})
			// Loans
			.addCase(fetchLoans.pending, (s) => {
				s.loans.status = 'loading';
				s.loans.error = null;
			})
			.addCase(fetchLoans.fulfilled, (s, a) => {
				s.loans.status = 'success';
				s.loans.data = a.payload;
				s.loans.error = null;
			})
			.addCase(fetchLoans.rejected, (s, a) => {
				s.loans.status = 'error';
				s.loans.error = a.payload || 'Failed to load loans';
			})
			// Deposits
			.addCase(fetchDeposits.pending, (s) => {
				s.deposits.status = 'loading';
				s.deposits.error = null;
			})
			.addCase(fetchDeposits.fulfilled, (s, a) => {
				s.deposits.status = 'success';
				s.deposits.data = a.payload;
				s.deposits.error = null;
			})
			.addCase(fetchDeposits.rejected, (s, a) => {
				s.deposits.status = 'error';
				s.deposits.error = a.payload || 'Failed to load deposits';
			})
			// NPA
			.addCase(fetchNPA.pending, (s) => {
				s.npa.status = 'loading';
				s.npa.error = null;
			})
			.addCase(fetchNPA.fulfilled, (s, a) => {
				s.npa.status = 'success';
				s.npa.data = a.payload;
				s.npa.error = null;
			})
			.addCase(fetchNPA.rejected, (s, a) => {
				s.npa.status = 'error';
				s.npa.error = a.payload || 'Failed to load NPA';
			})
			// Products
			.addCase(fetchProducts.pending, (s) => {
				s.products.status = 'loading';
				s.products.error = null;
			})
			.addCase(fetchProducts.fulfilled, (s, a) => {
				s.products.status = 'success';
				s.products.data = a.payload;
				s.products.error = null;
			})
			.addCase(fetchProducts.rejected, (s, a) => {
				s.products.status = 'error';
				s.products.error = a.payload || 'Failed to load products';
			})
			// Branches
			.addCase(fetchBranches.pending, (s) => {
				s.branches.status = 'loading';
				s.branches.error = null;
			})
			.addCase(fetchBranches.fulfilled, (s, a) => {
				s.branches.status = 'success';
				s.branches.data = a.payload;
				s.branches.error = null;
			})
			.addCase(fetchBranches.rejected, (s, a) => {
				s.branches.status = 'error';
				s.branches.error = a.payload || 'Failed to load branches';
			})
			// Data Quality
			.addCase(fetchDataQuality.pending, (s) => {
				s.dataQuality.status = 'loading';
				s.dataQuality.error = null;
			})
			.addCase(fetchDataQuality.fulfilled, (s, a) => {
				s.dataQuality.status = 'success';
				s.dataQuality.data = a.payload;
				s.dataQuality.error = null;
			})
			.addCase(fetchDataQuality.rejected, (s, a) => {
				s.dataQuality.status = 'error';
				s.dataQuality.error = a.payload || 'Failed to load data quality';
			})
			// Bank List
			.addCase(fetchBankList.pending, (s) => {
				s.bankList.status = 'loading';
				s.bankList.error = null;
			})
			.addCase(fetchBankList.fulfilled, (s, a) => {
				s.bankList.status = 'success';
				s.bankList.data = a.payload;
				s.bankList.error = null;
			})
			.addCase(fetchBankList.rejected, (s, a) => {
				s.bankList.status = 'error';
				s.bankList.error = a.payload || 'Failed to load banks';
			})
			// Bank detail
			.addCase(fetchBankDetail.pending, (s, a) => {
				const id = a.meta.arg.orgId;
				s.bankDetail[id] = { ...(s.bankDetail[id] ?? emptyCell()), status: 'loading', error: null };
			})
			.addCase(fetchBankDetail.fulfilled, (s, a) => {
				s.bankDetail[a.payload.orgId] = {
					data: a.payload.data,
					status: 'success',
					error: null,
				};
			})
			.addCase(fetchBankDetail.rejected, (s, a) => {
				const id = a.payload?.orgId ?? a.meta.arg.orgId;
				s.bankDetail[id] = {
					data: s.bankDetail[id]?.data ?? null,
					status: 'error',
					error: a.payload?.message || 'Failed to load bank detail',
				};
			})
			// Branch detail
			.addCase(fetchBranchDetail.pending, (s, a) => {
				const k = branchKey(a.meta.arg.orgId, a.meta.arg.branchIdLocal);
				s.branchDetail[k] = { ...(s.branchDetail[k] ?? emptyCell()), status: 'loading', error: null };
			})
			.addCase(fetchBranchDetail.fulfilled, (s, a) => {
				s.branchDetail[a.payload.key] = {
					data: a.payload.data,
					status: 'success',
					error: null,
				};
			})
			.addCase(fetchBranchDetail.rejected, (s, a) => {
				const k = a.payload?.key ?? branchKey(a.meta.arg.orgId, a.meta.arg.branchIdLocal);
				s.branchDetail[k] = {
					data: s.branchDetail[k]?.data ?? null,
					status: 'error',
					error: a.payload?.message || 'Failed to load branch detail',
				};
			})
			// Product detail
			.addCase(fetchProductDetail.pending, (s, a) => {
				const k = productKey(a.meta.arg.type, a.meta.arg.productCode, a.meta.arg.date);
				s.productDetail[k] = { ...(s.productDetail[k] ?? emptyCell()), status: 'loading', error: null };
			})
			.addCase(fetchProductDetail.fulfilled, (s, a) => {
				s.productDetail[a.payload.key] = {
					data: a.payload.data,
					status: 'success',
					error: null,
				};
			})
			.addCase(fetchProductDetail.rejected, (s, a) => {
				const k = a.payload?.key ?? productKey(a.meta.arg.type, a.meta.arg.productCode, a.meta.arg.date);
				s.productDetail[k] = {
					data: s.productDetail[k]?.data ?? null,
					status: 'error',
					error: a.payload?.message || 'Failed to load product detail',
				};
			});
	},
});

export default dashboardSlice.reducer;

// ── Selectors ───────────────────────────────────────────────────────────

export const selectOverview = (s: RootState): AsyncCell<OverviewResponse> => s.dashboard.overview;
export const selectLoans = (s: RootState): AsyncCell<LoansResponse> => s.dashboard.loans;
export const selectDeposits = (s: RootState): AsyncCell<DepositsResponse> => s.dashboard.deposits;
export const selectNPA = (s: RootState): AsyncCell<NPAResponse> => s.dashboard.npa;
export const selectProducts = (s: RootState): AsyncCell<ProductsResponse> => s.dashboard.products;
export const selectBranches = (s: RootState): AsyncCell<BranchesResponse> => s.dashboard.branches;
export const selectDataQuality = (s: RootState): AsyncCell<DataQualityResponse> => s.dashboard.dataQuality;
export const selectBankList = (s: RootState): AsyncCell<BankListResponse> => s.dashboard.bankList;

const EMPTY_BANK: AsyncCell<BankDetailResponse> = { data: null, status: 'idle', error: null };
const EMPTY_BRANCH: AsyncCell<BranchDetailResponse> = { data: null, status: 'idle', error: null };
const EMPTY_PRODUCT: AsyncCell<ProductDetailResponse> = { data: null, status: 'idle', error: null };

export const selectBankDetail =
	(orgId: number) =>
	(s: RootState): AsyncCell<BankDetailResponse> =>
		s.dashboard.bankDetail[orgId] ?? EMPTY_BANK;

export const selectBranchDetail =
	(orgId: number, branchIdLocal: string) =>
	(s: RootState): AsyncCell<BranchDetailResponse> =>
		s.dashboard.branchDetail[branchKey(orgId, branchIdLocal)] ?? EMPTY_BRANCH;

export const selectProductDetail =
	(type: ProductType, productCode: string, date?: string) =>
	(s: RootState): AsyncCell<ProductDetailResponse> =>
		s.dashboard.productDetail[productKey(type, productCode, date)] ?? EMPTY_PRODUCT;
